import { ComboBox } from "./ComboBox";
import { useEffect, useMemo, useRef } from "react";
import { DateTime, Duration, Interval } from "luxon";

type TimePickerInputProps = {
  readonly value?: DateTime<true>;
  readonly allowedInterval?: Interval<true>;
  readonly width: number;
  readonly onValueChange: (value: DateTime) => void;
};

export function TimePickerInput({
  value,
  width,
  allowedInterval,
  onValueChange: handleValueChange,
}: TimePickerInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const startOfSelectedDay = value?.startOf("day");

  const selectableTimes: DateTime[] = useMemo(() => {
    if (!startOfSelectedDay) return [];
    return startOfSelectedDay
      .until(startOfSelectedDay.endOf("day"))
      .splitBy({ minutes: 30 })
      .map((halfHourInterval) => halfHourInterval.start)
      .filter((dt) => dt !== null)
      .filter((dt) => !allowedInterval || containsIncludingEndpoints(allowedInterval, dt));
  }, [startOfSelectedDay, allowedInterval]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    input.addEventListener("pointerdown", handleSelectionChange);
    return () => {
      input.removeEventListener("pointerdown", handleSelectionChange);
    };
  }, []);

  const handleSelectionChange = (event: PointerEvent) => {
    setTimeout(() => {
      if (!event.target) {
        return;
      }
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }

      if (event.target.selectionStart === null || event.target.selectionStart <= 2) {
        event.target.setSelectionRange(0, 2);
      } else {
        event.target.setSelectionRange(3, 5);
      }
    }, 0);
  };

  return (
    <ComboBox
      ref={inputRef}
      value={value}
      width={width}
      onValueChange={(n) => n && handleValueChange(n)}
      onBlur={(n) => n && handleValueChange(n)}
      placeholder="00:00"
      items={selectableTimes}
      toNearestSelectableValue={toNearestSelectableValue}
      converters={{
        serialize: (s) => s?.toISO() ?? "",
        unserialize: (s) => {
          const duration = DateTime.fromISO(s ?? "");
          return duration.isValid ? duration : undefined;
        },
        format: (s) => (s ? s.toFormat("HH:mm") : ""),
        fromFormatted: (s) => (s ? startOfSelectedDay?.plus(Duration.fromISOTime(s)) : undefined),
        equals: (a, b) => a.equals(b),
      }}
    />
  );
}

function containsIncludingEndpoints(interval: Interval<true>, dateTime: DateTime): boolean {
  return interval.contains(dateTime) || interval.end.equals(dateTime);
}

// this method is used to find the nearest time to the selected time, so the scroll area can scroll to the correct position
function toNearestSelectableValue(s?: DateTime) {
  if (!s) return undefined;

  if (s.minute < 15) {
    return s.set({ minute: 0 });
  } else if (s.minute < 45) {
    return s.set({ minute: 30 });
  } else if (s.minute < 60) {
    return s.set({ hour: s.hour + 1, minute: 0 });
  }
  return undefined;
}
